import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';
import {
    Flex,
    Icon,
    PriorityIcon,
    TabMenuItem,
    TabsMenu,
    TabsSkinEnum
} from '@priority-software/priority-style-react';
import {TabData} from './types';
import {HiddenTabsDropdown} from './HiddenTabsDropdown';
import {isRTL} from '../../../../Localization';
import styles from './Tabs.module.scss';
import {createDataTestId} from '../../../../utils/dataTestId';

type Props = {
    onChange: (id: string, tabData: unknown) => void;
    data: Array<TabData>;
    activeId: string;
    onContextMenu: (e) => void;
};

export const ResponsiveTabs = (props: Props) => {
    const {data, onChange, activeId, onContextMenu} = props;
    const [showToolbar, setShowToolbar] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const scrollRef = useRef(null);
    const timerRef = useRef(null);

    const onSelect = useCallback(
        async (id: string) => {
            const tabData = findTabData(id, data);
            await onChange(id, tabData);
            const activeTabEl = ref.current.querySelector(`[data-id='${id}']`);

            if (activeTabEl) {
                scrollRef.current.scroll({
                    left: (activeTabEl as HTMLElement).offsetLeft,
                    behavior: 'smooth'
                });
            }
        },
        [data, onChange]
    );

    const onTabChange = useCallback(
        (id: string) => {
            const tabData = findTabData(id, data);
            onChange(id, tabData);
        },
        [onChange, data]
    );

    const handleScrollDirection = useCallback((direction: 'forward' | 'backward') => {
        const view = scrollRef.current;

        if (view) {
            const viewportWidth = view.offsetWidth || 0;
            const leftOffset = view.scrollLeft || 0;
            const step = viewportWidth / 2;

            view.scroll({
                left: direction === 'forward' ? leftOffset + step : leftOffset - step,
                behavior: 'smooth'
            });
        }
    }, []);

    const scrollTabLeft = useCallback(() => {
        handleScrollDirection(isRTL() ? 'forward' : 'backward');
    }, [handleScrollDirection]);

    const scrollTabRight = useCallback(() => {
        handleScrollDirection(isRTL() ? 'backward' : 'forward');
    }, [handleScrollDirection]);

    useLayoutEffect(() => {
        function handleToolbar() {
            const scrollView = scrollRef.current;

            if (scrollView) {
                const {offsetWidth, scrollWidth} = scrollView;
                setShowToolbar(scrollWidth > offsetWidth);
            }
        }

        function handleResize() {
            clearTimeout(timerRef.current);

            timerRef.current = setTimeout(handleToolbar, 250);
        }

        handleToolbar();

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div
            className={styles.component}
            ref={ref}
            {...createDataTestId('tab-menu-container', false)}
        >
            <TabsMenu skin={TabsSkinEnum.folder}>
                <div className={styles.scrollWrap}>
                    <div className={styles.inner} ref={scrollRef}>
                        {data.map(item => {
                            return (
                                <TabMenuItem
                                    key={item.id}
                                    isActive={item.id === activeId}
                                    onTabClick={onTabChange}
                                    text={item.title}
                                    onContextMenu={onContextMenu}
                                    componentClasses={styles.item}
                                    {...item}
                                />
                            );
                        })}
                    </div>
                </div>

                {showToolbar && (
                    <Flex componentClasses={styles.toolbar} inline>
                        <Icon
                            onClick={scrollTabLeft}
                            componentClasses={styles.navBtn}
                            icon={isRTL() ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft}
                        />
                        <Icon
                            onClick={scrollTabRight}
                            componentClasses={styles.navBtn}
                            icon={isRTL() ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight}
                        />

                        <HiddenTabsDropdown
                            componentClasses={styles.navBtn}
                            onChange={onSelect}
                            data={data}
                            activeId={activeId}
                            onContextMenu={onContextMenu}
                        />
                    </Flex>
                )}
            </TabsMenu>
        </div>
    );
};

const findTabData = (id: string, data) => {
    return data.find(item => item.id === id) || {};
};
